/**
 * イージング関数の定義
 */
export const easingFunctions = {
  linear: (t: number) => t,
  easeIn: (t: number) => t * t,
  easeOut: (t: number) => t * (2 - t),
  easeInOut: (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
  easeInQuad: (t: number) => t * t,
  easeOutQuad: (t: number) => t * (2 - t),
  easeInOutQuad: (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
  easeInCubic: (t: number) => t * t * t,
  easeOutCubic: (t: number) => (--t) * t * t + 1,
  easeInOutCubic: (t: number) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
  easeInQuart: (t: number) => t * t * t * t,
  easeOutQuart: (t: number) => 1 - (--t) * t * t * t,
  easeInOutQuart: (t: number) => t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t
};

export type EasingFunction = keyof typeof easingFunctions;

// スクロール中フラグを追加
let isScrolling = false;

/**
 * スムーススクロール関数（イージング対応）
 * @param targetPosition - スクロール先の位置
 * @param duration - アニメーション時間（ミリ秒）
 * @param easing - イージング関数名
 * @param callback - スクロール完了時のコールバック
 */
export const smoothScroll = (
  targetPosition: number,
  duration: number = 500,
  easing: EasingFunction = 'easeInOutQuad',
  callback?: () => void
) => {
  // スクロール中の場合は実行しない
  if (isScrolling) return;
  
  isScrolling = true;
  const startPosition = window.scrollY;
  const distance = targetPosition - startPosition;
  const startTime = performance.now();
  const easingFunction = easingFunctions[easing];

  const scrollAnimation = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // イージング関数を適用
    const easedProgress = easingFunction(progress);
    const currentPosition = startPosition + distance * easedProgress;
    
    window.scrollTo(0, currentPosition);
    
    if (progress < 1) {
      requestAnimationFrame(scrollAnimation);
    } else {
      // スクロール完了時にフラグをリセット
      isScrolling = false;
      // コールバックを実行
      if (callback) {
        callback();
      }
    }
  };
  
  requestAnimationFrame(scrollAnimation);
};

/**
 * 要素へのスムーススクロール（イージング対応）
 * @param element - スクロール先の要素
 * @param options - オプション設定
 */
export const smoothScrollToElement = (
  element: HTMLElement,
  options: {
    duration?: number;
    easing?: EasingFunction;
    offset?: number;
    callback?: () => void;
  } = {}
) => {
  const {
    duration = 500,
    easing = 'easeInOutQuad',
    offset = 0,
    callback
  } = options;

  // ヘッダーの高さを固定値で設定（PC: 110px, SP: 66px）
  const isSP = window.matchMedia('(max-width: 768px)').matches;
  const headerHeightBuffer = 20;
  const headerHeight = isSP ? 66 + headerHeightBuffer : 110 + headerHeightBuffer;
  
  // スクロール位置を計算
  const elementPosition = element.getBoundingClientRect().top;
  const targetPosition = elementPosition + window.scrollY - headerHeight - offset;
  
  smoothScroll(targetPosition, duration, easing, callback);
};

// 既に設定されているリスナーを追跡
const processedLinks = new WeakSet<Element>();

export const setupAnchorLinks = () => {
  const mainElement = document.querySelector('.ms-c-main');
  if (!mainElement) return;

  const anchorLinks = mainElement.querySelectorAll('a[href^="#"]');
  anchorLinks.forEach((anchorLink) => {
    // 既に処理済みのリンクはスキップ
    if (processedLinks.has(anchorLink)) return;
    processedLinks.add(anchorLink);
    
    // 既存のリスナーを削除（念のため）
    const newLink = anchorLink.cloneNode(true) as HTMLAnchorElement;
    anchorLink.parentNode?.replaceChild(newLink, anchorLink);
    
    newLink.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation(); // 他のリスナーの実行も止める
      
      const link = e.currentTarget as HTMLAnchorElement;
      const targetId = link.getAttribute('href');
      
      if (!targetId || targetId === '#') return;
      
      // スクロール中の場合は実行しない
      if (isScrolling) return;
      
      // ターゲット要素を取得
      const targetElement = document.querySelector(targetId) as HTMLElement;
      if (!targetElement) return;
      
      // スムーススクロール実行
      smoothScrollToElement(targetElement, {
        callback: () => {
          // URLのハッシュを更新（スクロール完了後）
          if (history.pushState) {
            history.pushState(null, '', targetId);
          }
        }
      });
    }, { capture: true }); // キャプチャフェーズで実行
  });
};

// デバッグ用：他のスクロールイベントを検出
export const debugScrollEvents = () => {
  // グローバルなクリックイベントを監視
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
      console.log('Global anchor click detected:', target);
    }
  }, true);

  // スクロールイベントを監視
  let scrollTimeout: number;
  window.addEventListener('scroll', () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = window.setTimeout(() => {
      if (!isScrolling) {
        console.log('Unexpected scroll detected at:', window.scrollY);
      }
    }, 100);
  });
};

/**
 * 指定されたコンテナ内のリンクから現在のURLのクエリパラメータを削除する
 * リンク先URLに現在のページのクエリパラメータが含まれている場合のみ削除する
 * @param containerSelectors - クエリパラメータを削除する対象のコンテナのセレクタ（配列）
 * @param delay - 実行までの遅延時間（ミリ秒）デフォルト: 1000ms
 */
export const cleanupQueryParamsFromLinks = (
  containerSelectors: string[] = ['#js-global-nav', '.f-footer'],
  delay: number = 1000
) => {
  setTimeout(() => {
    const currentUrl = new URL(window.location.href);
    const currentParams = Array.from(currentUrl.searchParams.keys());

    if (currentParams.length > 0) {
      const containers = document.querySelectorAll(containerSelectors.join(', '));

      containers.forEach(container => {
        const links = container.querySelectorAll('a[href]');

        links.forEach(link => {
          // data-keep-query-params 属性を持つリンクはスキップ
          if (link.hasAttribute('data-keep-query-params')) {
            return;
          }

          const href = link.getAttribute('href');
          if (href) {
            try {
              // 絶対URLまたは相対URLをパース
              let linkUrl: URL;
              if (href.startsWith('http://') || href.startsWith('https://')) {
                linkUrl = new URL(href);
              } else {
                // 相対パスの場合は、現在のoriginを使って絶対URLに変換
                linkUrl = new URL(href, window.location.origin);
              }

              let hasParams = false;

              // リンク先URLに現在のページのクエリパラメータが含まれているかチェック
              currentParams.forEach(param => {
                if (linkUrl.searchParams.has(param)) {
                  linkUrl.searchParams.delete(param);
                  hasParams = true;
                }
              });

              // パラメータを削除した場合のみhrefを更新
              if (hasParams) {
                // 元のhrefが相対パスだった場合は相対パスで返す
                if (!href.startsWith('http://') && !href.startsWith('https://')) {
                  const newUrl = linkUrl.pathname + linkUrl.search + linkUrl.hash;
                  link.setAttribute('href', newUrl);
                } else {
                  link.setAttribute('href', linkUrl.toString());
                }
              }
            } catch (e) {
              // URLのパースに失敗した場合（mailto:などの特殊なスキーム）
              // エラーは無視
            }
          }
        });
      });
    }
  }, delay);
};